import React, { useState } from 'react';
import {
  Box,
  Popover,
  MenuItem,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useClientCountry, SupportedCountry } from '../../hooks/useClientCountry';

interface CountrySelectorProps {
  variant?: 'dark' | 'light';
}

import { useAppTheme } from '../../contexts/ThemeContext';

export const CountrySelector: React.FC<CountrySelectorProps> = ({ variant = 'dark' }) => {
  const { country, flag, setCountry, COUNTRY_LABELS, SUPPORTED_COUNTRIES } = useClientCountry();
  const { isDark } = useAppTheme();
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  
  const isLightMode = isDark ? false : (variant === 'light');

  return (
    <>
      <Box
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1.25,
          py: 0.5,
          borderRadius: '20px',
          border: isLightMode ? '1px solid #e0e0e0' : '1px solid rgba(255,255,255,0.25)',
          cursor: 'pointer',
          color: isLightMode ? '#0f0f0f' : '#ffffff',
          flexShrink: 0,
          '&:hover': { bgcolor: isLightMode ? '#f3f2ef' : 'rgba(255,255,255,0.08)' },
        }}
      >
        <Typography sx={{ fontSize: '1rem', lineHeight: 1 }}>{flag}</Typography>
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, fontSize: '0.75rem', color: isLightMode ? '#565959' : '#ffffff' }}
        >
          {country}
        </Typography>
        <KeyboardArrowDownIcon sx={{ fontSize: 14, color: isLightMode ? '#565959' : '#ffffff' }} />
      </Box>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { mt: 0.5, minWidth: 160, borderRadius: '10px' } }}
      >
        {SUPPORTED_COUNTRIES.map((code: SupportedCountry) => (
          <MenuItem
            key={code}
            selected={country === code}
            onClick={() => { setCountry(code); setAnchor(null); }}
            sx={{
              gap: 1.5,
              py: 1.25,
              fontSize: '0.875rem',
              fontWeight: country === code ? 700 : 400,
            }}
          >
            <Typography sx={{ fontSize: '1.1rem' }}>{COUNTRY_LABELS[code].flag}</Typography>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 'inherit', lineHeight: 1.2 }}>
                {COUNTRY_LABELS[code].name}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {code}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Popover>
    </>
  );
};
